import { useQuery } from "@tanstack/react-query";

import { api } from "./api/client";
import { t } from "./i18n/i18n";
import { useDeployment } from "./map/units/useDeployment";
import { useUi } from "./state/ui";

/** Ennemi générique (registre B39, décision d'Alexis du 05/10/2026) : les ennemis de référence (noms, codes de
 *  faction, fiches d'aide qui les expliquent) ne s'affichent que pour les dossiers dont la force amie est une unité
 *  française fournie avec Generex. Ailleurs, l'ennemi est « l'ennemi générique ».
 *
 *  Le serveur décide seul (`generex_api/enemy_display.py`) et le dit par le drapeau `reference_enemy: false`, absent
 *  pour une force française, sur le dossier, la force, le dispositif, les modes d'action, le wargame et l'aperçu de
 *  transmission. Le client lit ce drapeau : il n'applique aucune règle à lui. */

/** Le drapeau du serveur désigne-t-il l'ennemi générique ? Seul `false` le fait : absent (force française, ou
 *  serveur qui ne connaît pas encore le drapeau), l'affichage reste celui d'aujourd'hui. */
export function isNeutral(flag: boolean | null | undefined): boolean {
  return flag === false;
}

/** Libellé de l'ennemi générique dans la langue de l'interface (« Ennemi générique », « Generic enemy »). */
export function genericEnemyLabel(): string {
  return t("Ennemi générique");
}

/** Contexte du dossier affiché : ennemis de référence montrés (vrai) ou ennemi générique (faux). Le dispositif
 *  affiché (celui du brouillon des Données initiales, ou du dossier enregistré) fait foi dès qu'il est reçu pour les
 *  paramètres en cours ; sinon le dossier ouvert. Sans dossier ni dispositif (accueil) : vrai (contexte par défaut,
 *  dossier d'exemple français). */
export function referenceEnemyShown(
  deployment: { reference_enemy?: false } | null | undefined,
  exercise: { reference_enemy?: false } | null | undefined,
): boolean {
  if (deployment) return !isNeutral(deployment.reference_enemy);
  return !isNeutral(exercise?.reference_enemy);
}

/** Bibliothèque des ordres de bataille : ennemis de référence montrés d'après le dossier d'où vient le lien (Données
 *  initiales), sinon d'après le dossier affiché (`shown`, accueil, palette de commandes, nouvel ordre de bataille) :
 *  jamais les ennemis des unités françaises sous le nom d'un dossier qui affronte l'ennemi générique. */
export function libraryReferenceEnemy(origin: { referenceEnemy?: boolean } | null | undefined, shown: boolean): boolean {
  return origin ? origin.referenceEnemy !== false : shown;
}

/** Ennemis de référence montrés dans le dossier affiché (`referenceEnemyShown`). */
export function useReferenceEnemy(): boolean {
  const exerciseId = useUi((s) => s.exerciseId);
  const welcome = useUi((s) => s.welcome);
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const { data: deployment, isPlaceholderData } = useDeployment();
  const exercise = exerciseId ? exercises?.find((e) => e.id === exerciseId) : undefined;
  // Accueil : contexte par défaut (dossier d'exemple français), quel que soit le dernier dispositif en cache.
  if (welcome) return true;
  // Dispositif des paramètres précédents (changement de dossier ou de brouillon) : il ne dit rien du dossier affiché.
  return referenceEnemyShown(isPlaceholderData ? null : deployment, exercise);
}

/** Dispositif affiché face à l'ennemi générique (drapeau du dispositif lui-même) : libellés de la composition, des
 *  rôles et de l'ordre de bataille sans « référence ». */
export function useGenericEnemy(): boolean {
  const { data } = useDeployment();
  return isNeutral(data?.reference_enemy);
}
